// @ts-check
// Cartão de entrada "Quando o bot começa" e o painel dos gatilhos (contrato de bots, seções 3 e 22.2): primeira
// mensagem, palavra-chave, conversa parada, disparo manual, resposta a um modelo, fora do horário e atendimento
// resolvido, cada um com os números de WhatsApp e o que o tipo pede. Até 10 gatilhos, separados por "ou".
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { Button } from '../../ui/button.js';
import { Checkbox } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { SegmentedControl } from '../../ui/segmented-control.js';
import { parseBotKeywords } from '../../lib/bot-editor-state.js';
import { BOT_KEYWORD_MATCH_MODES, BOT_TRIGGER_KINDS } from '../../../../shared/bots/bot-definition.js';
import { MAXIMUM_BOT_STALLED_MINUTES, MAXIMUM_BOT_TRIGGERS, MINIMUM_BOT_STALLED_MINUTES } from '../../../../shared/bots/bot-limits.js';
import { FlowSidePanel } from '../flows/flow-side-panel.js';
import { FlowField, FlowNotice } from '../flows/flow-ui.js';
import { BotNumberField, BotSelectField, useFieldId } from './bot-form-fields.js';
import { channelLabel, handoffTeams } from './bot-lookups.js';
import { BotPurposeChip, botProblemText, describeBotTrigger } from './bot-ui.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../../../shared/bots/bot-definition.js').BotDefinition} BotDefinition
 * @typedef {import('../../../../shared/bots/bot-definition.js').BotTrigger} BotTrigger
 * @typedef {import('../../lib/bot-validation.js').BotEditorProblem} BotEditorProblem
 * @typedef {import('./bot-lookups.js').BotLookupData} BotLookupData
 */

const AUTOMATIC_KINDS = new Set(['FIRST_MESSAGE', 'KEYWORD', 'STALLED', 'TEMPLATE_REPLY', 'OUT_OF_HOURS', 'TICKET_RESOLVED']);
const TRIGGER_ICONS = /** @type {Record<string, string>} */ ({
  FIRST_MESSAGE: 'IconMessageCirclePlus', KEYWORD: 'IconAbc', STALLED: 'IconHourglass', MANUAL: 'IconClick', TEMPLATE_REPLY: 'IconArrowBackUp',
  OUT_OF_HOURS: 'IconClock', TICKET_RESOLVED: 'IconCircleCheck',
});

/**
 * Gatilho novo do tipo, com o número quando só há um.
 * @param {string} kind @param {string[]} channelIds @returns {BotTrigger}
 */
export function newBotTrigger(kind, channelIds) {
  const channels = AUTOMATIC_KINDS.has(kind) && channelIds.length === 1 ? [...channelIds] : [];
  /** @type {BotTrigger} */
  const trigger = { id: crypto.randomUUID(), kind: /** @type {any} */ (kind) };
  if (AUTOMATIC_KINDS.has(kind)) trigger.channelIds = channels;
  if (kind === 'KEYWORD') Object.assign(trigger, { keywords: [], match: 'CONTAINS' });
  if (kind === 'STALLED') Object.assign(trigger, { minutes: 60, purposes: ['CAPTURE', 'SERVICE'] });
  if (kind === 'TEMPLATE_REPLY') Object.assign(trigger, { templateName: '', language: 'pt_BR', buttonTexts: [] });
  if (kind === 'OUT_OF_HOURS') Object.assign(trigger, { teamId: null });
  if (kind === 'TICKET_RESOLVED') Object.assign(trigger, { teamIds: [] });
  return trigger;
}

/**
 * @param {{ definition: BotDefinition, lookupData: BotLookupData, problems: BotEditorProblem[], isFocused?: boolean, isSelected?: boolean,
 *   isReadOnly?: boolean, onEdit?: () => void, startedCount?: number | null }} props
 */
export function BotEntryCard({ definition, lookupData, problems, isFocused = false, isSelected = false, isReadOnly = false, onEdit, startedCount = null }) {
  const hasError = problems.some((problem) => problem.severity === 'ERROR');
  const teamName = (/** @type {string} */ id) => lookupData.teams?.find((team) => team.id === id)?.name ?? null;
  const edit = (/** @type {MouseEvent} */ event) => {
    event.stopPropagation();
    onEdit?.();
  };
  return html`<div class="flow-entry-card bot-entry-card" role="treeitem" aria-level="1" aria-selected=${isSelected ? 'true' : 'false'} tabindex=${isFocused ? 0 : -1}
    data-focus-id="entry" data-problem=${hasError ? 'error' : undefined} data-selected=${isSelected ? 'true' : undefined} aria-label=${t('bots.entry.ariaLabel')}
    onClick=${isReadOnly ? undefined : edit} data-testid="bot-entry-card">
    <div class="flow-entry-header">
      <span class="flow-entry-title">${t('bots.entry.title')}</span>
      ${!isReadOnly && definition.triggers.length > 0
        ? html`<button type="button" class="flow-entry-edit" tabindex="-1" onClick=${edit}><${Icon} name="IconPencil" size=${14} /><span>${t('bots.entry.edit')}</span></button>`
        : null}
    </div>
    <div class="bot-entry-purpose"><${BotPurposeChip} purpose=${definition.purpose} /></div>
    ${definition.triggers.length === 0
      ? isReadOnly
        ? html`<p class="flow-entry-empty-text">${t('bots.entry.empty')}</p>`
        : html`<button type="button" class="flow-entry-select" tabindex="-1" onClick=${edit}><${Icon} name="IconPlus" size=${16} /><span>${t('bots.entry.add')}</span></button>`
      : html`<div class="flow-entry-box">
          ${definition.triggers.map((trigger, position) => {
            const described = describeBotTrigger(trigger, { channelLabel: (id) => channelLabel(lookupData, id), teamName });
            const triggerProblem = problems.find((problem) => problem.triggerId === trigger.id);
            return html`<div key=${trigger.id} class="flow-entry-trigger bot-entry-trigger" data-problem=${triggerProblem ? 'true' : undefined}>
              ${position > 0 ? html`<span class="flow-entry-or">${t('bots.entry.or')}</span>` : null}
              <p class="flow-entry-line"><${Icon} name=${TRIGGER_ICONS[trigger.kind] ?? 'IconPlayerPlay'} size=${14} /> ${described.title}</p>
              ${described.channels ? html`<p class="flow-entry-value">${described.channels}</p>` : null}
              ${described.details.map((detail) => html`<p class="flow-entry-more">${detail}</p>`)}
              ${triggerProblem ? html`<p class="bot-entry-problem">${botProblemText(triggerProblem)}</p>` : null}
            </div>`;
          })}
        </div>`}
    ${startedCount !== null ? html`<div class="flow-entry-counts"><span>${t('bots.entry.started', { count: startedCount })}</span></div>` : null}
  </div>`;
}

/**
 * Painel "Quando o bot começa".
 * @param {{ definition: BotDefinition, lookupData: BotLookupData, problems: BotEditorProblem[], onChange: (triggers: BotTrigger[]) => void, onClose: () => void }} props
 */
export function BotTriggersPanel({ definition, lookupData, problems, onChange, onClose }) {
  const [newKind, setNewKind] = useState('FIRST_MESSAGE');
  const triggers = definition.triggers;
  const channels = lookupData.channels ?? [];
  /** @param {string} id @param {Partial<BotTrigger>} patch */
  const update = (id, patch) => onChange(triggers.map((trigger) => (trigger.id === id ? { ...trigger, ...patch } : trigger)));
  const noTriggers = problems.find((problem) => problem.code === 'NO_TRIGGERS');
  return html`<${FlowSidePanel} title=${t('bots.entry.title')} help=${t('bots.entry.help')} onClose=${onClose} panelKey="entry" testId="bot-triggers-panel"
    backLabel=${t('bots.panel.backToBot')}
    footer=${html`<${Button} title=${t('bots.panel.close')} variant="secondary" size="small" onClick=${onClose} />`}>
    ${noTriggers ? html`<${FlowNotice} tone="warning" icon="IconAlertTriangle">${botProblemText(noTriggers)}<//>` : null}
    <ol class="bot-trigger-list">
      ${triggers.map((trigger, position) => html`<li key=${trigger.id} class="bot-trigger-item">
        <div class="bot-trigger-head">
          <span class="bot-trigger-title"><${Icon} name=${TRIGGER_ICONS[trigger.kind] ?? 'IconPlayerPlay'} size=${16} />${t(`bots.triggers.kinds.${trigger.kind}`)}</span>
          <button type="button" class="bot-option-remove" aria-label=${t('bots.triggers.remove', { number: position + 1 })}
            onClick=${() => onChange(triggers.filter((item) => item.id !== trigger.id))}><${Icon} name="IconTrash" size=${14} /></button>
        </div>
        <p class="flow-field-hint">${t(`bots.triggers.hints.${trigger.kind}`)}</p>
        <${BotTriggerFields} trigger=${trigger} channels=${channels} lookupData=${lookupData}
          problems=${problems.filter((problem) => problem.triggerId === trigger.id)} onChange=${(/** @type {Partial<BotTrigger>} */ patch) => update(trigger.id, patch)} />
      </li>`)}
    </ol>
    ${triggers.length < MAXIMUM_BOT_TRIGGERS
      ? html`<div class="bot-add-trigger">
          <${BotSelectField} label=${t('bots.triggers.addLabel')} value=${newKind} placeholder=${null}
            options=${BOT_TRIGGER_KINDS.map((kind) => ({ value: kind, label: t(`bots.triggers.kinds.${kind}`) }))} onChange=${(kind) => setNewKind(kind ?? 'FIRST_MESSAGE')} />
          <${Button} title=${t('bots.triggers.add')} icon="IconPlus" variant="secondary" size="small" data-testid="bot-add-trigger"
            onClick=${() => onChange([...triggers, newBotTrigger(newKind, channels.map((channel) => channel.id))])} />
        </div>`
      : html`<p class="flow-field-hint">${t('bots.triggers.limit', { maximum: MAXIMUM_BOT_TRIGGERS })}</p>`}
  <//>`;
}

/**
 * Campos de um gatilho pelo tipo.
 * @param {{ trigger: BotTrigger, channels: Array<{ id: string, label: string }>, lookupData: BotLookupData, problems: BotEditorProblem[],
 *   onChange: (patch: Partial<BotTrigger>) => void }} props
 */
function BotTriggerFields({ trigger, channels, lookupData, problems, onChange }) {
  const keywordsId = useFieldId('bot-keywords');
  const templateId = useFieldId('bot-template');
  const buttonsId = useFieldId('bot-template-buttons');
  /** @param {string} field */
  const errorOf = (field) => {
    const problem = problems.find((candidate) => candidate.details?.field === field);
    return problem ? botProblemText(problem) : null;
  };
  const selectedChannels = trigger.channelIds ?? [];
  const teams = handoffTeams(lookupData.teams);
  return html`<div class="bot-trigger-fields">
    ${AUTOMATIC_KINDS.has(trigger.kind)
      ? html`<${FlowField} label=${t('bots.triggers.channels')} error=${errorOf('channelIds')}>
          ${channels.length === 0
            ? html`<p class="flow-field-hint">${t('bots.triggers.noChannels')}</p>`
            : html`<div class="bot-rule-checks" role="group" aria-label=${t('bots.triggers.channels')}>
                ${channels.map((channel) => html`<label key=${channel.id} class="flow-checkbox-row">
                  <${Checkbox} checked=${selectedChannels.includes(channel.id)} ariaLabel=${channel.label}
                    onChange=${(/** @type {boolean} */ checked) => onChange({ channelIds: checked ? [...selectedChannels, channel.id] : selectedChannels.filter((id) => id !== channel.id) })} />
                  <span>${channel.label}</span>
                </label>`)}
              </div>`}
        <//>`
      : null}
    ${trigger.kind === 'KEYWORD'
      ? html`<${FlowField} label=${t('bots.triggers.keywords')} htmlFor=${keywordsId} error=${errorOf('keywords')} hint=${t('bots.triggers.keywordsHint')}>
          <input id=${keywordsId} class="text-input" value=${(trigger.keywords ?? []).map((word) => word.toLowerCase()).join(', ')} data-testid="bot-trigger-keywords"
            onChange=${(/** @type {Event} */ event) => onChange({ keywords: parseBotKeywords(/** @type {HTMLInputElement} */ (event.currentTarget).value) })} />
        <//>
        <div class="flow-field">
          <span class="flow-field-label">${t('bots.triggers.match')}</span>
          <${SegmentedControl} label=${t('bots.triggers.match')} value=${trigger.match ?? 'CONTAINS'}
            options=${BOT_KEYWORD_MATCH_MODES.map((value) => ({ value, label: t(`bots.triggers.matchModes.${value}`) }))}
            onChange=${(/** @type {'EXACT' | 'CONTAINS'} */ match) => onChange({ match })} />
        </div>`
      : null}
    ${trigger.kind === 'STALLED'
      ? html`<${BotNumberField} label=${t('bots.triggers.minutes')} value=${trigger.minutes ?? null} minimum=${MINIMUM_BOT_STALLED_MINUTES} maximum=${MAXIMUM_BOT_STALLED_MINUTES}
          suffix=${t('bots.forms.minutesSuffix')} error=${errorOf('minutes')} onChange=${(minutes) => onChange({ minutes: minutes ?? undefined })} />
        <div class="bot-rule-checks" role="group" aria-label=${t('bots.triggers.purposes')}>
          ${['CAPTURE', 'SERVICE'].map((purpose) => html`<label key=${purpose} class="flow-checkbox-row">
            <${Checkbox} checked=${(trigger.purposes ?? []).includes(/** @type {any} */ (purpose))} ariaLabel=${t(`bots.threadPurposes.${purpose}`)}
              onChange=${(/** @type {boolean} */ checked) => {
                const current = /** @type {string[]} */ (trigger.purposes ?? []);
                onChange({ purposes: /** @type {any} */ (checked ? [...current, purpose] : current.filter((item) => item !== purpose)) });
              }} />
            <span>${t(`bots.threadPurposes.${purpose}`)}</span>
          </label>`)}
        </div>`
      : null}
    ${trigger.kind === 'TEMPLATE_REPLY'
      ? html`<${FlowField} label=${t('bots.triggers.templateName')} htmlFor=${templateId} error=${errorOf('templateName')} hint=${t('bots.triggers.templateHint')}>
          <input id=${templateId} class="text-input" value=${trigger.templateName ?? ''}
            onInput=${(/** @type {InputEvent} */ event) => onChange({ templateName: /** @type {HTMLInputElement} */ (event.currentTarget).value.trim() })} />
        <//>
        <${FlowField} label=${t('bots.triggers.buttonTexts')} htmlFor=${buttonsId} hint=${t('bots.triggers.buttonTextsHint')}>
          <input id=${buttonsId} class="text-input" value=${(trigger.buttonTexts ?? []).join(', ')}
            onChange=${(/** @type {Event} */ event) => onChange({ buttonTexts: /** @type {HTMLInputElement} */ (event.currentTarget).value.split(',').map((text) => text.trim()).filter(Boolean) })} />
        <//>`
      : null}
    ${trigger.kind === 'OUT_OF_HOURS'
      ? html`<${BotSelectField} label=${t('bots.forms.team')} value=${trigger.teamId} placeholder=${t('bots.forms.chooseTeam')} error=${errorOf('teamId')}
          options=${teams.map((team) => ({ value: team.id, label: team.name }))} onChange=${(teamId) => onChange({ teamId: teamId ?? undefined })} testId="bot-trigger-team" />`
      : null}
    ${trigger.kind === 'TICKET_RESOLVED'
      ? html`<${FlowField} label=${t('bots.triggers.teams')} hint=${t('bots.triggers.teamsHint')}>
          <div class="bot-rule-checks" role="group" aria-label=${t('bots.triggers.teams')}>
            ${teams.map((team) => html`<label key=${team.id} class="flow-checkbox-row">
              <${Checkbox} checked=${(trigger.teamIds ?? []).includes(team.id)} ariaLabel=${team.name}
                onChange=${(/** @type {boolean} */ checked) => onChange({ teamIds: checked ? [...(trigger.teamIds ?? []), team.id] : (trigger.teamIds ?? []).filter((id) => id !== team.id) })} />
              <span>${team.name}</span>
            </label>`)}
          </div>
        <//>`
      : null}
    ${trigger.kind === 'MANUAL' ? html`<p class="flow-field-hint">${t('bots.triggers.manualHint')}</p>` : null}
  </div>`;
}
